import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import ProfilePicture from '../../../components/profilePicture/ProfilePicture';
import Icon from '../../../components/icon/Icon';
import Filter from '../../../components/filter/Filter';
import Dropdown from '../../../components/dropdown/Dropdown';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import LoadMore from '../../../components/loadMore/loadMore';
import { getScheduleColor } from '../../../utils/specialityUtils';
import Tag from '../../../components/tag/Tag';
import styles from './RotationSummary.module.scss';

const LIMIT = 10;


const STATUS_VARIANTS = {
  'Completed': 5,
  'In-Progress': 2,
  'Scheduled': 3,
};

const STATUS_ICON_CLASS = {
  'Completed': 'iconCompleted',
  'In-Progress': 'iconInProgress',
  'Scheduled': 'iconScheduled',
};

const RotationSummary = () => {
  const [rotations, setRotations] = useState([]);
  const [years, setYears] = useState([]);
  const [allSpecialties, setAllSpecialties] = useState([]);
  const [activeYearId, setActiveYearId] = useState(null);
  const [selectedSpecialityId, setSelectedSpecialityId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  // trigger controls fetching; activeYearId/selectedSpecialityId control UI only
  const [trigger, setTrigger] = useState({ page: 1, yearId: null, specialityId: null });
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchRotations = async () => {
      if (trigger.page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      try {
        const params = { limit: LIMIT, page: trigger.page };
        if (trigger.yearId) params.yearId = trigger.yearId;
        if (trigger.specialityId) params.specialities = [trigger.specialityId];
        const response = await axiosClient.get('/program-schedules/student/rotations', { params });
        const raw = response.data;
        const items = Array.isArray(raw) ? raw : (raw?.data || []);
        if (trigger.page === 1) {
          setRotations(items);
        } else {
          setRotations(prev => [...prev, ...items]);
        }
        setTotalPages(raw?.totalPages || 1);
        const yearsList = raw?.years || [];
        if (yearsList.length && !trigger.yearId) {
          setYears(yearsList);
          const current = yearsList.find(y => y.isCurrent);
          if (current) setActiveYearId(current.id);
        }
        if (trigger.page === 1) {
          setAllSpecialties(raw?.specialities || []);
        }
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    };
    fetchRotations();
  }, [trigger, showToast]);

  const handleYearChange = (id) => {
    setActiveYearId(id);
    setTrigger(prev => ({ page: 1, yearId: id, specialityId: prev.specialityId }));
  };

  const handleSpecialityChange = (id) => {
    setSelectedSpecialityId(id);
    setTrigger(prev => ({ page: 1, yearId: prev.yearId, specialityId: id || null }));
  };

  const loadMore = () => {
    setTrigger(prev => ({ ...prev, page: prev.page + 1 }));
  };

  const [activePopup, setActivePopup] = useState(null);
  const popupRef = useRef(null);

  useEffect(() => {
    if (!activePopup) return;
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setActivePopup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePopup]);

  const POPUP_WIDTH = 240;
  const SPACING = 8;

  const openPopup = (e, key, title, type, items) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = rect.right + SPACING + POPUP_WIDTH > window.innerWidth
      ? rect.left - POPUP_WIDTH - SPACING
      : rect.right + SPACING;
    const opensUp = rect.top > window.innerHeight / 2;
    const top = rect.top + window.scrollY;
    setActivePopup({ key, title, type, items, position: { top, left: left + window.scrollX }, opensUp });
  };

  return (
    <>
    <div className={styles.container}>
      {isLoading && <LoadingOverlay />}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          <h2 className={styles.title}>Rotation Summary</h2>
          <div className={styles.filter}>
            <Filter
              key={activeYearId}
              filters={years.map(year => ({ label: year.year, value: year.id }))}
              selected={activeYearId}
              onChange={handleYearChange}
            />
          </div>
        </div>
        <div className={styles.dropdown}>
          <Dropdown
            label="Specialty"
            options={[
              { label: 'All Specialties', value: null },
              ...allSpecialties.map(s => ({ label: s.name, value: s.id })),
            ]}
            value={selectedSpecialityId}
            onChange={handleSpecialityChange}
          />
        </div>
      </div>

      {!isLoading && rotations.length === 0 && (
        <div className={styles.emptyState}>
          <p>You have no rotations at this time.</p>
        </div>
      )}

      <div className={styles.rotationList}>
        {rotations.filter(rotation => rotation.specialities?.length > 0).map((rotation, index) => {
          const statusVariant = STATUS_VARIANTS[rotation.status] ?? 3;
          return (
            <div key={rotation.id} className={styles.rotationCard}>
              <div className={styles.rotationHeader}>
                <div className={styles.rotationTitleRow}>
                  <span className={styles[STATUS_ICON_CLASS[rotation.status]]}><Icon name="calendar" /></span>
                  <h3 className={styles.rotationTitle}>Rotation {index + 1}</h3>
                  <Tag text={rotation.status} variant={statusVariant} rounded={true} />
                </div>
                <div className={styles.dateRow}>
                  <div className={styles.dateItem}>
                    <span className={styles.dateLabel}>Start Date</span>
                    <span className={styles.dateValue}>{format(new Date(rotation.startDate), 'M/dd/yyyy')}</span>
                  </div>
                  <div className={styles.dateItem}>
                    <span className={styles.dateLabel}>End Date</span>
                    <span className={styles.dateValue}>{format(new Date(rotation.endDate), 'M/dd/yyyy')}</span>
                  </div>
                </div>
              </div>

              <div className={styles.divider} />

              <div className={styles.specialtiesGrid}>
                {rotation.specialities.map(specialty => {
                  const visibleInstructors = specialty.instructors.slice(0, 3);
                  const extraInstructors = specialty.instructors.length - 3;
                  const firstSite = specialty.clinicalSites[0];
                  const extraSites = specialty.clinicalSites.length - 1;

                  return (
                    <div key={specialty.id} className={styles.specialtyItem}>
                      <div className={styles.specialtyName}>
                        <span
                          className={`${styles.dot} ${styles[getScheduleColor(specialty.id)]}`}
                        />
                        <span>{specialty.name}</span>
                      </div>

                      <div className={styles.specialtyDetails}>
                        <div className={styles.detailGroup}>
                          <span className={styles.detailLabel}>Instructor</span>
                          <div className={styles.instructorAvatarsWrapper}>
                            <div
                              className={styles.instructorAvatars}
                              onClick={(e) => specialty.instructors.length > 0 && openPopup(e, `inst-${rotation.id}-${specialty.id}`, 'Instructors', 'instructor', specialty.instructors)}
                            >
                              {visibleInstructors.map(inst => (
                                <div key={inst.id} className={styles.avatarWrapper}>
                                  <ProfilePicture
                                    firstName={inst.firstName}
                                    lastName={inst.lastName}
                                    src={inst.profilePicture}
                                    size="xs"
                                  />
                                </div>
                              ))}
                              {extraInstructors > 0 && (
                                <span className={styles.extraCount}>+{extraInstructors}</span>
                              )}
                              {specialty.instructors.length === 0 && (
                                <span className={styles.siteName}>-</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className={styles.detailGroup}>
                          <span className={styles.detailLabel}>Clinical Site</span>
                          <div
                            className={styles.clinicalSiteValue}
                            onClick={(e) => specialty.clinicalSites.length > 1 && openPopup(e, `site-${rotation.id}-${specialty.id}`, 'Clinical Sites', 'site', specialty.clinicalSites)}
                            style={{ cursor: specialty.clinicalSites.length > 1 ? 'pointer' : 'default' }}
                          >
                            {firstSite ? (
                              <>
                                <span className={styles.siteName}>{firstSite.name}</span>
                                {extraSites > 0 && (
                                  <span className={styles.extraCount}>+{extraSites}</span>
                                )}
                              </>
                            ) : (
                              <span className={styles.siteName}>-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {(isLoadingMore || trigger.page < totalPages) && (
          <LoadMore isLoading={isLoadingMore} onClick={loadMore} />
        )}
      </div>
    </div>

    {activePopup && createPortal(
      <div
        className={`${styles.instructorPopup} ${activePopup.opensUp ? styles.opensUp : ''}`}
        ref={popupRef}
        style={activePopup.position}
      >
        <div className={styles.popupHeader}>
          <span className={styles.popupTitle}>{activePopup.title}</span>
          <button className={styles.popupClose} onClick={() => setActivePopup(null)}>✕</button>
        </div>
        <div className={styles.popupList}>
          {activePopup.type === 'instructor' ? (
            activePopup.items.map(inst => (
              <div key={inst.id} className={styles.popupItem}>
                <ProfilePicture
                  firstName={inst.firstName}
                  lastName={inst.lastName}
                  src={inst.profilePicture}
                  size="xsm"
                />
                <span className={styles.popupName}>{inst.firstName} {inst.lastName}</span>
              </div>
            ))
          ) : (
            activePopup.items.map(site => (
              <div key={site.id} className={styles.popupItem}>
                <span className={styles.popupName}>{site.name}</span>
              </div>
            ))
          )}
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default RotationSummary;
